/**
 * Util checks if the given string is of HTML type or not
 * @param str string to check
 * @returns boolean
 */
export const isHTMLString = (str: string) => {
  const htmlRegex = /<[^>]*>/; // This regular expression matches any HTML tags
  return htmlRegex.test(str);
};

/**
 * Checks if a given string contains a space character.
 * @param {string} input - The string to check.
 * @returns {boolean} Returns true if the string contains a space, false otherwise.
 */
export const checkContainsSpaces = ({ input }: { input: string }) => {
  return input.trim().indexOf(" ") > -1;
};

/**
 * Extracts plain text content from an HTML string.
 *
 * @param {string} htmlString - The input HTML string.
 * @returns {string} - The extracted text content.
 */
export const extractTextContentFromHtml = (htmlString: string): string => {
  const parser = new DOMParser();
  const doc = parser?.parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};

export const generateRandomString = (len = 8) => {
  let text = "";
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < len; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length));

  return text;
};

/**
 * replace all characters with a given character
 * @param str - string to be replaced
 * @param options.char - character to replace with. default: `"_"`
 * @param options.replaceConsecutive - default: `true`
 */
export const replaceAllSpecialChars: (
  str: string,
  options?: { char?: string; replaceConsecutive?: boolean },
) => string = (str, options) => {
  const defaultOptions = {
    char: "_",
    replaceConsecutive: true,
  };

  const char = options?.char || defaultOptions.char;
  const returnVal = str.replace(/[^a-zA-Z0-9]/g, char);

  const replaceConsecutive =
    options?.replaceConsecutive || defaultOptions.replaceConsecutive;
  if (replaceConsecutive) {
    returnVal.replace(/_{2,}/g, char); // replace consecutive `_____` by just one `_`
  }

  return returnVal;
};

const VOWELS = new Set(["a", "e", "i", "o", "u"]);
/**
 * function takes a word and a count as parameters and returns the plural form of the word based on the count.
 * @param word  (string): The word that you want to pluralize.
 * @param count (number): The count associated with the word, which determines whether the plural form should be used.
 * @returns string representing the plural form of the input word based on the given count. If the count is 1, it returns the original word unchanged.
 */
export const pluralise = (word: string, count: number) => {
  if (!word) return "";

  const len = word.length;
  const hasYAtEnd = word[len - 1].toLowerCase() === "y";
  const hasVowelBeforeY = VOWELS.has(word[len - 2].toLowerCase());

  if (hasYAtEnd && !hasVowelBeforeY) {
    // If the word ends with 'y' and there is no vowel before 'y'
    const splittedWord = word.slice(0, -1);
    return count > 1 ? `${splittedWord}ies` : word;
  }

  // Default case for other words
  return count > 1 ? `${word}s` : word;
};

/**
 * referenced from https://stackoverflow.com/a/29858893
 * @returns capitalises each word in a string
 */
export const capitaliseWords = (str: string) => {
  const regex = /(\b[a-z](?!\s))/g;
  return str.replace(regex, (x) => {
    return x.toUpperCase();
  });
};

export const checkIsStringEmpty = (str: string | null | undefined = "") =>
  !str?.length;
